import {
  FiltersMap,
  KeyedItem,
  MultiCollectionSupport,
  TaskState,
} from '@wix/bex-core';
import { NestedCollectionNodeState } from './NestedCollectionNodeState';
import {
  action,
  computed,
  createAtom,
  makeObservable,
  runInAction,
} from 'mobx';
import { TableColumn } from '../../model';
import { NestedTableNestedModeState } from './NestedTableNestedModeState';
import { ReactNode } from 'react';
import { NestedTableNodeStateBIReporter } from './NestedTableNodeStateBIReporter';
import { NestedTableLevelDescriptor } from './NestedTableLevelDescriptor';

export interface NestedTableDataItem<
  C extends string,
  T,
  F extends FiltersMap,
> {
  state: NestedTableNodeState<C, T, F>;
  originalKeyedItem: KeyedItem<T>;
}

interface NestedTableSkeletonItem<C extends string, T, F extends FiltersMap> {
  state: NestedTableNodeState<C, T, F>;
  originalKeyedItem: undefined;
}

export type NestedTableItem<C extends string, T, F extends FiltersMap> =
  | NestedTableDataItem<C, T, F>
  | NestedTableSkeletonItem<C, T, F>;

export type NestedTableKeyedItem<
  C extends string,
  T,
  F extends FiltersMap,
> = KeyedItem<NestedTableItem<C, T, F>>;

export type RenderMainColumn<T> = (item: T) => {
  title?: string;
  subtitle?: string;
  image?: ReactNode;
};

export type NestedTableNestColumns<C extends string, T> = Partial<
  Record<C, TableColumn<T>['render']>
>;

export interface NestedTableNodeStateParams<
  C extends string,
  T,
  F extends FiltersMap,
> {
  readonly nestedTable: NestedTableNestedModeState<C>;
  readonly node: NestedCollectionNodeState<T, F>;
  readonly levelDescriptor: NestedTableLevelDescriptor<C, T, F>;
  readonly parentNode: NestedTableNodeState<C, any, any> | null;
  readonly parent: KeyedItem<unknown> | null;
}

export class NestedTableNodeState<C extends string, T, F extends FiltersMap> {
  readonly nestedTable;
  readonly node;

  readonly levelDescriptor;
  readonly parentNode;
  readonly parent;
  readonly bi: NestedTableNodeStateBIReporter<C, T, F>;

  readonly multi;

  _rowNum?: number;

  readonly initTask = new TaskState();

  readonly parentKeyToChildrenMap = new Map<
    string,
    NestedTableNodeState<C, any, any>
  >();

  readonly parentKeyToChildrenMapAtom = createAtom(
    'parentKeyToChildrenMapAtom',
  );

  constructor(params: NestedTableNodeStateParams<C, T, F>) {
    this.nestedTable = params.nestedTable;
    this.node = params.node;
    this.parentNode = params.parentNode;
    this.parent = params.parent;
    this.levelDescriptor = params.levelDescriptor;
    this.bi = new NestedTableNodeStateBIReporter(this);

    this.multi = new MultiCollectionSupport({
      collections: [this.node.collection],
    });

    makeObservable(this, {
      keyedItems: computed,
      _keyedItemsIfExpanded: computed,
      _isNotExpandableWhenEmpty: computed,
      init: action,
      createNode: action,
      _preRegisterAll: action,
      _toggleExpand: action,
      _notify: action,
      _notifyAll: action,
      _keyedItemsMap: computed,
    });

    this._register();
  }

  get collection() {
    return this.node.collection;
  }

  get depth() {
    return this.levelDescriptor.depth;
  }

  get _expanded() {
    return this.node._expanded;
  }

  get columns() {
    return this.levelDescriptor.columns;
  }

  get renderMainColumn() {
    return this.levelDescriptor.renderMainColumn;
  }

  get showHoverHighlight() {
    return this.node.hoveredOrParentHovered;
  }

  _changeHover(hovered: boolean) {
    this.node._changeHover(hovered);
  }

  get showEmptyState() {
    return this.node.collection.showEmptyState;
  }

  get showLoadingState() {
    return (
      this.node.collection.showInitialLoader ||
      this.node.collection.showRefreshLoader
    );
  }

  get showErrorState() {
    return this.node.collection.showErrorState;
  }

  get errorStatus() {
    return this.node.collection.result.errorStatus;
  }

  retryErrorState = () => this.node.collection.retryErrorState();

  get _isNotExpandableWhenEmpty() {
    const {
      nestedTable: { isFetchAllAborted },
      node: {
        collection: { showEmptyState },
      },
    } = this;
    return !isFetchAllAborted && showEmptyState;
  }

  get _keyedItemsIfExpanded(): NestedTableKeyedItem<C, any, any>[] {
    const {
      parent,
      nestedTable: { isFetchAllAborted },
      _expanded,
      node: {
        collection: { showRefreshLoader, showEmptyState, showErrorState },
      },
    } = this;

    if (!_expanded) {
      return [];
    }

    return [
      ...this.keyedItems,
      ...(parent &&
      isFetchAllAborted &&
      (showRefreshLoader || showEmptyState || showErrorState)
        ? new Array(1).fill(null).map((_, index) => ({
            id: `placeholder-states-${parent.key}`,
            key: `placeholder-states-${parent.key}`,
            index,
            pageIndex: 0,
            indexWithinPage: index,
            item: {
              state: this,
              originalKeyedItem: undefined,
            },
          }))
        : []),
    ];
  }

  get _keyedItemsMap(): Map<string, NestedTableKeyedItem<C, any, any>> {
    return new Map(
      this.keyedItems.map((keyedItem) => [keyedItem.key, keyedItem]),
    );
  }

  get keyedItems(): NestedTableKeyedItem<C, any, any>[] {
    const { node, parentKeyToChildrenMap, parentKeyToChildrenMapAtom } = this;
    const {
      collection: {
        result: { keyedItems },
      },
    } = node;

    parentKeyToChildrenMapAtom.reportObserved();

    return keyedItems
      .flatMap((keyedItem) => [
        {
          ...keyedItem,
          item: {
            state: this,
            originalKeyedItem: keyedItem,
          },
        },
        ...(parentKeyToChildrenMap.get(keyedItem.key)?._keyedItemsIfExpanded ??
          []),
      ])
      .map((keyedItem, index) => ({ ...keyedItem, index }));
  }

  getKeyedItem(key: string) {
    return this._keyedItemsMap.get(key);
  }

  getKeyedItemByIndex(ind: number) {
    return this.keyedItems[ind];
  }

  getCollectionSnapshot() {
    const {
      collection: {
        result: { originQuery },
      },
      nestedTable: { toolbar },
    } = this;

    return {
      viewShown: toolbar.viewsState?.views.currentView?.id,
      tabShown: toolbar.getCurrentTabName(),
      sortOrderApplied: originQuery.sort?.length
        ? JSON.stringify(originQuery.sort)
        : undefined,
      filtersApplied: JSON.stringify(originQuery.filtersKey),
      filteredListSizeShown: this.collection.result.total,
    };
  }

  _register() {
    const { parent, parentNode } = this;

    if (parent == null || parentNode == null) {
      return;
    }

    parentNode.parentKeyToChildrenMap.set(parent.key, this);

    return () => {
      parentNode.parentKeyToChildrenMap.delete(parent.key);
    };
  }

  async _toggleExpand() {
    const promise = this.node._toggleExpand();
    this.bi.reportExpandCollapse();
    this.parentNode?.parentKeyToChildrenMapAtom.reportChanged();
    await promise;
    runInAction(() => {
      this.parentNode?.parentKeyToChildrenMapAtom.reportChanged();
    });
  }

  async _preRegisterAll() {
    const { node } = this;
    const {
      collection: {
        result: { keyedItems },
      },
    } = node;

    await Promise.all(
      keyedItems.map(async (keyedItem) => {
        const state = this.createNode({ parent: keyedItem });
        if (state == null) {
          return;
        }
        const dispose = state.init();
        await state.initTask.status.promise;
        dispose();
      }),
    );
  }

  _notify() {
    const { parentKeyToChildrenMapAtom } = this;
    parentKeyToChildrenMapAtom.reportChanged();
  }

  _notifyAll() {
    const { parentKeyToChildrenMap } = this;
    this._notify();
    parentKeyToChildrenMap.forEach((state) => {
      state._notifyAll();
    });
  }

  init({ skipCollectionInit }: { skipCollectionInit?: boolean } = {}) {
    const { node, initTask, nestedTable } = this;

    const disposers = [node.init({ skipCollectionInit })];

    initTask.runOnce(async () => {
      node.initTask.runOnce();
      await node.initTask.status.promise;

      await runInAction(async () => {
        if (!nestedTable.isFetchAllAborted && nestedTable.expandAll) {
          await this._preRegisterAll();
          if (this.depth === 0) {
            this._notifyAll();
          }
        }
        return;
      });
    });

    return () => {
      disposers.forEach((d) => d?.());
    };
  }

  createNode({
    parent,
  }: {
    parent: KeyedItem<any>;
  }): NestedTableNodeState<C, any, any> | null {
    const { nestedTable, node, depth, parentKeyToChildrenMap } = this;

    const existing = parentKeyToChildrenMap.get(parent.key);

    if (existing != null) {
      return existing;
    }

    const nextLevelDescriptor = nestedTable.levels[depth + 1];

    if (nextLevelDescriptor == null) {
      return null;
    }

    const nextLevelNode = node._createNode(parent, {
      levelDescriptor: nextLevelDescriptor,
    });

    if (nextLevelNode == null) {
      return null;
    }

    return new NestedTableNodeState({
      parent,
      parentNode: this,
      nestedTable,
      node: nextLevelNode,
      levelDescriptor: nextLevelDescriptor,
    });
  }
}
